import Phaser from 'phaser';
import { VIEWPORT_W } from '../../constants';
import type { StageModel } from '../types';

function addFullLayer(
  scene: Phaser.Scene,
  root: Phaser.GameObjects.Container,
  key: string,
  depth: number,
  model: StageModel,
) {
  const img = scene.add
    .image(0, model.bgBleedY / 2, key)
    .setOrigin(0.5, 0.5)
    .setDisplaySize(model.worldWidth, model.worldHeight + model.bgBleedY)
    .setDepth(depth);
  root.add(img);
  return img;
}

function createCloudTile(scene: Phaser.Scene, x: number, model: StageModel) {
  const tileW = VIEWPORT_W;
  const img = scene.add
    .image(x, model.bgBleedY / 2, 'level-clouds')
    .setDisplaySize(tileW, model.worldHeight + model.bgBleedY)
    .setDepth(1)
    .setOrigin(0.5, 0.5);
  return img;
}

export function createStageView(scene: Phaser.Scene, model: StageModel) {
  let currentModel = model;
  const root = scene.add.container(currentModel.worldWidth / 2, currentModel.worldHeight / 2);

  const backdrop = scene.add
    .rectangle(0, 0, currentModel.worldWidth + 4, currentModel.worldHeight + currentModel.bgBleedY, 0x9fd0e8)
    .setDepth(-1);
  root.add(backdrop);

  addFullLayer(scene, root, 'level-sky', 0, currentModel);

  // Cloud strip tiles must be spaced by the full texture width (1400), not half —
  // half-spacing made recycle hops non-seamless and caused a visible jump.
  const tileW = VIEWPORT_W;
  const spacing = tileW;
  const clouds: Phaser.GameObjects.Image[] = [];
  const tileCount = Math.max(3, currentModel.cloudCount);

  for (let i = 0; i < tileCount; i++) {
    // Center of first tile starts near the left edge of the world.
    const img = createCloudTile(scene, -tileW + spacing / 2 + i * spacing, currentModel);
    root.add(img);
    clouds.push(img);
  }

  addFullLayer(scene, root, 'level-world', 2, currentModel);

  function recycleClouds() {
    const period = tileCount * spacing;
    const offLeft = -currentModel.worldWidth / 2 - tileW / 2;

    for (const cloud of clouds) {
      // Wrap by the full tiled period so the pattern never snaps mid-texture.
      while (cloud.x < offLeft) {
        cloud.x += period;
      }
    }
  }

  function update(delta: number) {
    const dx = currentModel.cloudDriftPxPerSec * (delta / 1000);
    for (const cloud of clouds) {
      cloud.x -= dx;
    }
    recycleClouds();
  }

  function setModel(next: StageModel) {
    currentModel = next;
    root.setPosition(next.worldWidth / 2, next.worldHeight / 2);
    backdrop.setSize(next.worldWidth + 4, next.worldHeight + next.bgBleedY);
  }

  function syncFromScroll(_scrollX: number) {
    // Stage spans the full world width — no panel visibility toggle.
  }

  return {
    root,
    setModel,
    syncFromScroll,
    update,
    destroy() {
      root.destroy();
    },
  };
}
